import * as React from "react"
import { Col, Row } from "react-grid-system"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import { SlideOutHeight } from "../atoms/animations/SlideOutHeight"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import { getAllOrgIds } from "../../utils/helpers"
import { OrganizationType } from "../../utils/models"
import H3 from "../atoms/typography/H3"
import TextButton from "../atoms/buttons/TextButton"
import { ArrowDropDownIcon } from "../atoms/icons/ArrowDropDownIcon"
import { ArrowDropUpIcon } from "../atoms/icons/ArrowDropUpIcon"
import Checkbox from "../atoms/inputs/Checkbox"
import { useClickOutside } from "../../hooks/useClickOutside"
import SearchField from "../atoms/inputs/SearchField"

interface SuborganizationsDropdownProps {
  org?: OrganizationType
  onClickSuborg: (id?: number, checkedMain?: boolean) => void
  selected?: Array<number>
  onDeselectAllSuborgs: () => void
}

const ContentContainer = styled.div`
  width: 100%;
  background-color: ${(props) => props.theme.colors.white};
  filter: drop-shadow(0px 0px 4px rgba(119, 119, 119, 0.25));
  padding: ${(props) => props.theme.spacings.extreme};
  border-radius: ${(props) => props.theme.borders.softRadius};
`

const TextButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`

const AllSuborganizationsDropdown: React.FunctionComponent<SuborganizationsDropdownProps> = ({
  org,
  onClickSuborg,
  selected,
  onDeselectAllSuborgs,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)

  const [filterValue, setFilterValue] = React.useState<string>("")

  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false))

  const onClick = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const title = selected ? `${selected.length} ${t("suborganizations")}` : t("all_suborganizations")

  const checkSelected = (id: number | string) => {
    return !selected ? true : selected.includes(parseFloat(id.toString()))
  }

  const onClickBabyOrg = (id: number | string) => {
    onClickSuborg(parseFloat(id.toString()))
  }

  const onCheckAllSuborgs = () => {
    !selected || allSelected ? onDeselectAllSuborgs() : onClickSuborg(undefined, true)
  }

  const allSuborgIds = getAllOrgIds(org?.childOrganizations?.edges ?? [])
  const allSelected = allSuborgIds.length === selected?.length

  return (
    <section ref={ref}>
      <Row justify="center">
        <TextButton title={title} icon={open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />} onClick={onClick} />
      </Row>
      <HorizontalSpacer spacing={1} />
      <SlideOutHeight isVisible={open}>
        <ContentContainer>
          <HorizontalSpacer spacing={1} />
          <Col lg={4} md={8} style={{ padding: 0 }}>
            <TextButtonsContainer>
              <SearchField placeholder={`${t("Filter")}...`} onChange={(e) => setFilterValue(e)} />
              <Spacer spacing={4} />
              <TextButton title="Select all" onClick={onCheckAllSuborgs} />
              <Spacer spacing={4} />
              <TextButton title="Deselect all" onClick={onDeselectAllSuborgs} />
            </TextButtonsContainer>
          </Col>
          <HorizontalSpacer spacing={1} />
          <Row align="center" style={{ padding: "0 7px" }}>
            {org?.childOrganizations?.edges?.map((babyOrg: OrganizationType) => {
              const matchesFilter = babyOrg.name.toLowerCase().includes(filterValue.toLowerCase())
              return (
                <Col
                  md={3}
                  key={babyOrg.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "0.5rem",
                  }}
                >
                  <Checkbox
                    disabled={!matchesFilter}
                    faded={!matchesFilter}
                    checked={checkSelected(babyOrg.id)}
                    onChange={() => onClickBabyOrg(babyOrg.id)}
                  />
                  <Spacer spacing={1} />
                  <H3 opacity={!matchesFilter ? ".3" : "1.0"}>{babyOrg.name}</H3>
                </Col>
              )
            })}
          </Row>
          <HorizontalSpacer spacing={1} />
        </ContentContainer>
      </SlideOutHeight>
    </section>
  )
}

export default AllSuborganizationsDropdown
